import type { FirestoreSemanticData } from "reedy-data/models";

type Character = NonNullable<FirestoreSemanticData["characters"]>[number];

export type SummarySection =
  | "overview"
  | "summary"
  | "worldSetting"
  | "characters";

// Two settings can share a location, and the model puts no constraints on genres.
const uniqueTags = (tags: string[]): string[] => [...new Set(tags)];

export const genreTags = (genres: FirestoreSemanticData["genres"]): string[] =>
  uniqueTags(
    (genres ?? []).map((genre) => genre.trim()).filter((genre) => genre),
  );

export const worldSettingTags = (
  worldSettings: FirestoreSemanticData["worldSettings"],
): string[] => {
  if (!worldSettings) return [];

  const tags: string[] = [];
  for (const setting of worldSettings.settings ?? []) {
    const locationLabel = setting.locationLabel?.trim();
    if (locationLabel) tags.push(locationLabel);
    const timePeriodLabel = setting.timePeriodLabel?.trim();
    if (timePeriodLabel) tags.push(timePeriodLabel);
  }
  const socialStructureLabel = worldSettings.socialStructureLabel?.trim();
  if (socialStructureLabel) tags.push(socialStructureLabel);
  const metaphysicsLabel = worldSettings.metaphysicsLabel?.trim();
  if (metaphysicsLabel) tags.push(metaphysicsLabel);
  return uniqueTags(tags);
};

export const hasOverviewContent = (
  semanticData: FirestoreSemanticData | undefined,
): boolean => {
  if (!semanticData) return false;

  return (
    genreTags(semanticData.genres).length > 0 ||
    Boolean(semanticData.synopsis?.trim()) ||
    worldSettingTags(semanticData.worldSettings).length > 0
  );
};

export const charactersWithContent = (
  characters: FirestoreSemanticData["characters"],
): Character[] =>
  (characters ?? []).filter((character) => {
    const fields = [
      character.name,
      character.age,
      character.pronouns,
      character.role,
      character.description,
      ...(character.aliases ?? []),
    ];
    return fields.some(
      (value) => typeof value === "number" || Boolean(value?.trim().length),
    );
  });

export const getAvailableSummarySections = (
  semanticData: FirestoreSemanticData | undefined,
): SummarySection[] => {
  if (!semanticData) return [];

  const sections: SummarySection[] = [];
  if (hasOverviewContent(semanticData)) sections.push("overview");
  if (semanticData.paragraphHistory?.some((paragraph) => paragraph.trim())) {
    sections.push("summary");
  }
  if (semanticData.worldSettings) sections.push("worldSetting");
  if (charactersWithContent(semanticData.characters).length) {
    sections.push("characters");
  }
  return sections;
};

export const splitParagraphs = (text: string): string[] =>
  text
    .split(/\r?\n/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

export const withParagraphKeys = (
  paragraphs: string[],
): { key: string; text: string }[] => {
  const occurrences = new Map<string, number>();
  return paragraphs.map((text) => {
    const occurrence = occurrences.get(text) ?? 0;
    occurrences.set(text, occurrence + 1);
    return { key: `${text}-${occurrence}`, text };
  });
};

export const semanticDataLanguage = (
  semanticData: FirestoreSemanticData,
): string | undefined => {
  const { outputLanguage } = semanticData.rawPayload;
  if (typeof outputLanguage !== "string") return undefined;

  try {
    return Intl.getCanonicalLocales(outputLanguage.trim())[0];
  } catch {
    return undefined;
  }
};
